import React, { useState, useEffect, useRef } from 'react';
import dot from "../resources/mydot.png";
import cadmusLogo from "../resources/cadmus_light.svg";

const isMobile = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

const AnimatedScoreboard = ({ currentScore, onAnimationComplete, dashboardSate, showLogo = false }) => {
  const [displayScore, setDisplayScore] = useState(currentScore || 0);
  const [prevScore, setPrevScore] = useState(currentScore || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCounting, setIsCounting] = useState(false);
  const scoreRef = useRef(currentScore || 0);
  const [initialized, setInitialized] = useState(false);
  const animationFrameRef = useRef(null);
  const timeoutRef = useRef(null);
  const [isWarning, setIsWarning] = useState(false);
  const displayScoreRef = useRef(currentScore || 0);

  const [logoPhase, setLogoPhase] = useState("hidden"); // hidden | in | hold | out

  useEffect(() => {
    if (showLogo) {
      setLogoPhase("in");
      const holdTimer = setTimeout(() => setLogoPhase("hold"), 600);
      const outTimer  = setTimeout(() => setLogoPhase("out"), 2800);
      const doneTimer = setTimeout(() => setLogoPhase("hidden"), 3600);
      return () => { clearTimeout(holdTimer); clearTimeout(outTimer); clearTimeout(doneTimer); };
    } else {
      setLogoPhase("hidden");
    }
  }, [showLogo]);

  useEffect(() => {
    setIsWarning(dashboardSate !== "ok" && dashboardSate !== "sleeping");
  }, [dashboardSate]);

  useEffect(() => {
    if (currentScore === undefined || currentScore === null) return;

    if (!initialized) {
      setInitialized(true);
      if (scoreRef.current === 0 && currentScore === 0) {
        setDisplayScore(0);
        setPrevScore(0);
      } else {
        setPrevScore(0);
        scoreRef.current = currentScore;
        animateToNewScore(currentScore);
      }
    } else if (currentScore !== scoreRef.current) {
      scoreRef.current = currentScore;
      animateToNewScore(currentScore);
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentScore, initialized]);

  const animateToNewScore = (newScore) => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const startScore = displayScoreRef.current;
    setPrevScore(startScore);
    setIsAnimating(true);
    setIsCounting(true);

    const duration = 1000;
    const startTime = Date.now();

    const animateScore = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuad = (t) => t * (2 - t);
      const easedProgress = easeOutQuad(progress);
      const current = Math.floor(startScore + (newScore - startScore) * easedProgress);
      displayScoreRef.current = current;
      setDisplayScore(current);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateScore);
      } else {
        displayScoreRef.current = newScore;
        setDisplayScore(newScore);
        setIsCounting(false);

        timeoutRef.current = setTimeout(() => {
          setIsAnimating(false);
          if (onAnimationComplete) onAnimationComplete();
        }, 1800);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateScore);
  };

  const base      = isWarning ? "#8C2240" : "#6644BB";
  const neonColor = isWarning ? "#7A1E38" : "#5535CC";
  const neonMid   = isWarning ? "#621428" : "#3D22AA";
  const neonOuter = isWarning ? "#4A0A1C" : "#271488";
  const neonWide  = isWarning ? "#2E0410" : "#140A55";
  const blob      = isWarning ? "#A82C4B" : "#6C48D8";

  const fontStyle = {
    fontFamily: '"DM Sans"',
    fontSize: "128px",
    fontWeight: "300",
    lineHeight: "100%",
    letterSpacing: "-2.56px",
  };

  const logoVisible = logoPhase !== "hidden";
  const scoreOpacity = logoVisible ? 0 : 1;

  const renderNeonLogo = () => {
    if (!logoVisible) return null;

    const logoOpacity = logoPhase === "in" ? 1 : logoPhase === "hold" ? 1 : 0;
    const logoScale   = logoPhase === "in" ? 1 : logoPhase === "hold" ? 1 : 0.85;
    const transition  = logoPhase === "in"
      ? "opacity 0.5s ease-out, transform 0.5s ease-out, filter 0.5s ease-out"
      : logoPhase === "out"
      ? "opacity 0.8s ease-in, transform 0.8s ease-in, filter 0.8s ease-in"
      : "none";

    const size = 96;

    return (
      <div style={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        zIndex: 10, pointerEvents: "none",
      }}>
        <img src={cadmusLogo} alt="" style={{
          position: "absolute", width: size, height: size,
          opacity: logoOpacity * 0.6, transform: `scale(${logoScale * 1.1})`,
          filter: "blur(32px) brightness(3)", mixBlendMode: "plus-lighter", transition,
        }} />
        <img src={cadmusLogo} alt="" style={{
          position: "absolute", width: size, height: size,
          opacity: logoOpacity * 0.9, transform: `scale(${logoScale})`,
          filter: "blur(12px) brightness(4)", mixBlendMode: "plus-lighter", transition,
        }} />
        <img src={cadmusLogo} alt="" style={{
          position: "absolute", width: size, height: size,
          opacity: logoOpacity, transform: `scale(${logoScale})`,
          filter: "blur(3px) brightness(3)", mixBlendMode: "plus-lighter", transition,
        }} />
        <img src={cadmusLogo} alt="Cadmus" style={{
          position: "absolute", width: size, height: size,
          opacity: logoOpacity, transform: `scale(${logoScale})`,
          filter: "brightness(2)", transition,
        }} />
      </div>
    );
  };

  const renderCurrentScore = () => {
    const scoreStr = displayScore.toString();

    if (isMobile) {
      const mobileLayerBase = {
        ...fontStyle,
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        whiteSpace: "nowrap",
      };
      return (
        // Glow via text-shadow i.p.v. lagen met filter: blur(): iOS (WebKit,
        // dus ook Chrome op iOS) tekende die geblurde lagen met harde randen
        // (een paarse streep boven de cijfers)
        <div style={{ position: "absolute", inset: 0 }}>
          <div style={{
            ...mobileLayerBase,
            color: isWarning ? "#FF8AAA" : "#D4AAFF",
            textShadow: isWarning
              ? "0 0 2px #9B2040, 0 0 6px #9B2040, 0 0 18px #7A1E38"
              : "0 0 2px #7B54F0, 0 0 6px #7B54F0, 0 0 18px #5535CC",
          }}>{scoreStr}</div>
        </div>
      );
    }

    const layer = (color, blur, top = "0") => (
      <div style={{ ...fontStyle, color, position: "absolute", filter: `blur(${blur})`, mixBlendMode: "plus-lighter", opacity: 1, top, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
        {scoreStr}
      </div>
    );
    return (
      <div style={{ position: "relative", height: "128px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {layer(neonWide,  "96px", "48px")}
        {layer(neonOuter, "56px", "28px")}
        {layer(neonOuter, "36px", "18px")}
        {layer(neonMid,   "20px", "10px")}
        {layer(neonMid,   "12px",  "6px")}
        {layer(neonColor,  "6px",  "3px")}
        {layer(neonColor,  "2px",  "1px")}
        {layer(base,      "0.5px",   "0")}
      </div>
    );
  };

  const renderAfterglowDigits = () => {
    if (!isAnimating || isMobile) return null;

    const maxLength = Math.max(prevScore.toString().length, displayScore.toString().length);
    const prevStr = prevScore.toString().padStart(maxLength, "0");
    const currStr = displayScore.toString().padStart(maxLength, "0");

    const changedPositions = {};
    for (let i = 0; i < maxLength; i++) {
      if (prevStr[i] !== currStr[i]) changedPositions[i] = true;
    }
    if (Object.keys(changedPositions).length === 0) return null;

    return (
      <div style={{
        position: "absolute", left: 0, right: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "128px", zIndex: 5, pointerEvents: "none",
      }}>
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          {Array.from({ length: maxLength }).map((_, i) => {
            const digit = prevStr[i];
            if (!changedPositions[i]) {
              return <span key={i} style={{ ...fontStyle, visibility: "hidden" }}>{digit}</span>;
            }

            return (
              <span key={i} style={{ position: "relative", display: "inline-block" }}>
                <span style={{ ...fontStyle, visibility: "hidden" }}>{digit}</span>

                <span style={{
                  ...fontStyle, color: neonOuter,
                  position: "absolute", top: "24px", left: "50%", transform: "translateX(-50%)",
                  filter: "blur(48px)", mixBlendMode: "plus-lighter", opacity: 1,
                  whiteSpace: "nowrap",
                  animation: "glowHaloOut 1.2s ease-out forwards",
                  userSelect: "none",
                }}>{digit}</span>

                <span style={{
                  ...fontStyle, color: neonMid,
                  position: "absolute", top: "11px", left: "50%", transform: "translateX(-50%)",
                  filter: "blur(22px)", mixBlendMode: "plus-lighter", opacity: 1,
                  whiteSpace: "nowrap",
                  animation: "glowMidOut 0.9s ease-out forwards",
                  userSelect: "none",
                }}>{digit}</span>

                <span style={{
                  ...fontStyle, color: neonColor,
                  position: "absolute", top: "4px", left: "50%", transform: "translateX(-50%)",
                  filter: "blur(12px)", mixBlendMode: "plus-lighter", opacity: 1,
                  whiteSpace: "nowrap",
                  animation: "glowTightOut 0.6s ease-out forwards",
                  userSelect: "none",
                }}>{digit}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: "411px", margin: "0 auto", overflow: "hidden", height: "192px", borderRadius: "24px" }}>

      {/* zIndex 1 — achtergrond: zwart + blob glow */}
      <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 1, borderRadius: "24px", border: "1px solid #000000", backgroundColor: "#000000", overflow: "hidden" }}>
        {isMobile ? (
          // Zelfde gloed als de twee geblurde ellipsen hieronder, maar als
          // radial-gradient: iOS tekende die grote blurs met harde randen
          // (vlakken in twee tinten paars)
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse 230px 150px at 50% 25px, ${blob} 0%, ${blob}99 45%, ${blob}00 100%), radial-gradient(ellipse 380px 240px at 50% 0px, ${blob} 0%, ${blob}80 40%, ${blob}00 100%)` }} />
        ) : (
          <>
            <div style={{ position: "absolute", top: "-50px", left: "50%", transform: "translateX(-50%)", width: "300px", height: "150px", borderRadius: "50%", background: blob, filter: "blur(64px)", opacity: 1, flexShrink: 0 }} />
            <div style={{ position: "absolute", top: "-113px", left: "50%", transform: "translateX(-50%)", width: "450px", height: "225px", borderRadius: "50%", background: blob, filter: "blur(112.5px)", opacity: 1, flexShrink: 0 }} />
          </>
        )}
      </div>

      {/* zIndex 2 — score */}
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2,
        opacity: scoreOpacity,
        transition: logoVisible ? "opacity 0.4s ease-in" : "opacity 0.6s ease-out",
      }}>
        {renderCurrentScore()}
      </div>

      {/* zIndex 3 — dot overlay */}
      <div className="dot-overlay" style={{
        width: "100%", height: "100%",
        position: "absolute", top: 0, left: 0,
        backgroundImage: `url(${dot})`,
        backgroundPosition: "0 0",
        backgroundRepeat: "repeat",
        backgroundSize: "2px 2px",
        // Scherpe stippen op retina i.p.v. uitgesmeerd
        imageRendering: "pixelated",
        opacity: 0.1,
        zIndex: 3,
        pointerEvents: "none",
        borderRadius: "24px",
      }} />

      {/* zIndex 4 — afterglow */}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 4 }}>
        {renderAfterglowDigits()}
      </div>

      {/* zIndex 10 — logo reveal */}
      {renderNeonLogo()}

      <style>{`
        @keyframes glowHaloOut {
          0%   { opacity: 0.7; filter: blur(48px); transform: translateX(-50%) scale(1.0); }
          20%  { opacity: 1;   filter: blur(56px); transform: translateX(-50%) scale(1.15); }
          100% { opacity: 0;   filter: blur(72px); transform: translateX(-50%) scale(1.5); }
        }
        @keyframes glowMidOut {
          0%   { opacity: 0.8; filter: blur(22px); }
          20%  { opacity: 1;   filter: blur(28px); }
          100% { opacity: 0;   filter: blur(44px); }
        }
        @keyframes glowTightOut {
          0%   { opacity: 0.9; filter: blur(8px); }
          15%  { opacity: 1;   filter: blur(12px); }
          100% { opacity: 0;   filter: blur(24px); }
        }
      `}</style>
    </div>
  );
};

export default AnimatedScoreboard;
